import './screens.css';
import { el, setText, text } from './walkout.js';
import { needs, needsIf } from './contract.js';

/**
 * The only screen that goes up in the middle of a round.
 *
 * Every other one lives in a gap the game already has — the walkout holds the
 * intro, the aftermath comes after the bell, the hub sits between two fights.
 * This one interrupts, which makes it the one screen that has to actually
 * **stop** something: `main.js` skips `advance` while it is up, so the round
 * clock, the ladder's churn, the train and the crowd all hold still together.
 * A pause that let any of them keep running would be a pause that cost you
 * something.
 *
 * Three doors.
 *
 * **Resume** is the ordinary one and Escape is the same door, which is why
 * Escape opens *and* closes it.
 *
 * **Sound** is here because the master had nowhere else to live. It has been
 * persisted with the career since step 63 and reachable only from the debug
 * overlay, which is a developer's window — a player who wants the game quiet
 * should not have to find a panel behind a backtick.
 *
 * **Forfeit** takes two clicks, and the second lapses. Same shape as throwing a
 * career away (gotcha 63), for the same reason: a one-click button that ends
 * the round sitting under the one that resumes it is a round lost to a
 * mis-click.
 *
 * What a forfeit *is* is the interesting part. It does not invent an outcome —
 * it drains the player's consciousness, and `fight.outCold` notices a body that
 * has given out and announces the knockout it was already going to announce.
 * The campaign records an ordinary loss with no blow behind it, the aftermath
 * says *the body gave out*, and heat scores it as a plain win for the other
 * man. Giving up is a way of losing this game already had; this is a button
 * that does it on purpose.
 */
export function createPause({ player, match, audio = null, onResume, container = document.body }) {
  needs(match, ['live'], { who: 'the pause menu', what: 'match' });
  needs(player?.health, ['drainConsciousness'], { who: 'the pause menu', what: 'player.health' });
  // The one genuinely optional handle: a browser with no `AudioContext` gets a
  // null device and this screen simply has no sound panel on it.
  needsIf(audio, ['volume', 'muted', 'crowd', 'setVolume', 'setMuted', 'toggle'], {
    who: 'the pause menu',
    what: 'audio',
  });
  const root = el('div', 'screen screen--pause');
  root.hidden = true;

  root.append(text('div', 'screen__kind', 'paused'));

  const doors = el('div', 'pause__doors');
  const resume = el('button', 'ui-button');
  resume.dataset.tone = 'go';
  resume.textContent = 'resume';
  doors.append(resume);
  root.append(doors);

  // --- sound ----------------------------------------------------------------
  const sound = el('div', 'pause__panel');
  sound.append(text('div', 'ui-label', 'sound'));
  const level = text('div', 'pause__level', '');
  const steps = el('div', 'pause__steps');
  const mute = el('button', 'ui-button pause__step');
  mute.addEventListener('click', () => {
    audio?.toggle();
    draw();
  });
  steps.append(mute);
  for (const step of [0, 0.35, 0.7, 1]) {
    const button = el('button', 'ui-button pause__step');
    button.textContent = `${Math.round(step * 100)}`;
    button.addEventListener('click', () => {
      audio?.setVolume(step);
      if (step > 0) audio?.setMuted(false);
      draw();
    });
    steps.append(button);
  }
  sound.append(level, steps);
  if (audio) root.append(sound);

  // --- the way out ----------------------------------------------------------
  const out = el('div', 'pause__out');
  const forfeit = el('button', 'ui-button');
  forfeit.dataset.tone = 'warn';
  const forfeitNote = text('div', 'pause__note', '');
  out.append(forfeit, forfeitNote);
  root.append(out);
  root.append(text('div', 'screen__hint', 'escape to resume'));

  container.append(root);

  let up = false;
  /** Whether the forfeit button is armed, and the timer that disarms it. */
  let armed = false;
  let lapse = null;

  function disarm() {
    armed = false;
    clearTimeout(lapse);
    lapse = null;
  }

  function draw() {
    if (audio) {
      setText(
        level,
        audio.muted ? 'muted' : `${Math.round(audio.volume * 100)}% · crowd ${Math.round(audio.crowd * 100)}%`
      );
      mute.textContent = audio.muted ? 'unmute' : 'mute';
      mute.dataset.tone = audio.muted ? 'go' : '';
    }
    forfeit.textContent = armed ? 'really forfeit?' : 'forfeit';
    setText(
      forfeitNote,
      armed
        ? 'the round is recorded as a loss and you drop a place'
        : 'give the round up — it counts, like any other loss'
    );
  }

  function show() {
    if (up || !match.live) return false;
    disarm();
    draw();
    root.hidden = false;
    up = true;
    resume.focus?.();
    return true;
  }

  function hide() {
    if (!up) return false;
    disarm();
    up = false;
    root.hidden = true;
    return true;
  }

  function resumeFight() {
    if (!hide()) return false;
    onResume?.();
    return true;
  }

  /**
   * Gives the round up, through the door the rules already have.
   *
   * No new outcome and no new flag: the body gives out, `fight.outCold` sees a
   * fighter who is finished, and everything downstream — the campaign, the
   * ladder, heat, the aftermath — handles it as the ordinary attrition loss it
   * is. The screen is closed first so the frame is running again when the
   * knockout lands and the round can actually end.
   */
  function giveUp() {
    if (!up) return false;
    if (!armed) {
      armed = true;
      draw();
      lapse = setTimeout(() => {
        disarm();
        draw();
      }, 4000);
      return false;
    }
    resumeFight();
    player.health.drainConsciousness(Number.MAX_SAFE_INTEGER);
    return true;
  }

  function onKey(event) {
    if (event.key !== 'Escape') return;
    // Not over a screen that is already up: the walkout, the hub and the
    // aftermath are their own gaps and there is nothing to pause.
    if (!up && !match.live) return;
    event.preventDefault();
    if (up) resumeFight();
    else show();
  }

  resume.addEventListener('click', resumeFight);
  forfeit.addEventListener('click', giveUp);
  window.addEventListener('keydown', onKey);

  return {
    element: root,
    get up() {
      return up;
    },
    show,
    resume: resumeFight,
    /** Twice, like the button. Exposed so a headless run can do it in one call. */
    forfeit: () => {
      giveUp();
      return giveUp();
    },
    dispose() {
      disarm();
      window.removeEventListener('keydown', onKey);
      root.remove();
    },
  };
}
